// Single loader for pdfjs-dist. Consolidates three near-identical copies
// from apps/web tool runners, examples/pdf-browser, and the parse-validator
// embed. Tests mock this module so unit tests don't actually parse PDFs.

import { Data, Effect } from 'effect';
import type { PdfSource, ViewportLike } from '../types';

export class PdfViewerRuntimeError extends Data.TaggedError('PdfViewerRuntimeError')<{
  readonly operation: 'load' | 'resolve';
  readonly message: string;
}> {}

const pdfViewerRuntimeCauses = new WeakMap<PdfViewerRuntimeError, unknown>();

function pdfViewerFailure(operation: PdfViewerRuntimeError['operation'], cause: unknown) {
  const error = new PdfViewerRuntimeError({
    operation,
    message: `Unable to ${operation} the PDF.`,
  });
  pdfViewerRuntimeCauses.set(error, cause);
  return error;
}

function restorePdfViewerError<A>(program: Effect.Effect<A, PdfViewerRuntimeError>) {
  return program.pipe(
    Effect.catch((error) => Effect.fail(pdfViewerRuntimeCauses.get(error) ?? error)),
  );
}

export interface LoadedPage {
  pageNumber: number;
  viewport: ViewportLike;
  /**
   * Render the page to a 2D canvas context. Pass an AbortSignal so callers
   * can cancel in-flight renders during rapid page navigation; without this,
   * pdf.js throws "Cannot use the same canvas during rendering" or paints
   * the wrong page into the canvas.
   */
  render(ctx: CanvasRenderingContext2D, signal?: AbortSignal): Promise<void>;
}

export interface LoadedPdf {
  numPages: number;
  getPage(n: number): Promise<LoadedPage>;
}

export interface LoadOptions {
  /** Render scale; applied per-page when getPage(n).viewport is computed. Default 1.5. */
  scale?: number;
}

const DEFAULT_SCALE = 1.5;

// pdfjs-dist is a peer dep. We dynamic-import so this module works in
// environments that don't have it installed (e.g. typecheck-only consumers).
type PdfjsModule = typeof import('pdfjs-dist');
let pdfjsPromise: Promise<PdfjsModule> | null = null;

async function loadPdfjs(): Promise<PdfjsModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((mod) => {
      // Configure the worker. In SSR / Node tests this is a no-op since
      // pdfjs falls back to fake-worker on main thread when workerSrc fails.
      if (typeof window !== 'undefined' && mod.GlobalWorkerOptions) {
        if (!mod.GlobalWorkerOptions.workerSrc) {
          // Codex P1: derive the worker URL from the installed pdfjs-dist
          // version (peer range allows ^4 || ^5). A hardcoded 4.0.379 worker
          // would cause pdf.js to reject rendering when consumers install
          // v5 (e.g. apps/web pins ^5.7.284). jsdelivr mirrors npm
          // directly so any published version resolves.
          const version = mod.version ?? '4.0.379';
          mod.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
        }
      }
      return mod;
    });
  }
  return pdfjsPromise;
}

async function fetchSourceBytes(source: PdfSource): Promise<Uint8Array> {
  // Codex P2 (round 6): pdf.js may TRANSFER (detach) the underlying
  // ArrayBuffer of the bytes we hand to `getDocument({data})`. If the caller
  // reuses the same source for a reload (e.g. switching scale/mode), the
  // second call would see an empty buffer. Always pass pdf.js a fresh copy
  // and leave the caller's buffer untouched.
  if ('bytes' in source) {
    const src = source.bytes instanceof Uint8Array ? source.bytes : new Uint8Array(source.bytes);
    const copy = new Uint8Array(src.byteLength);
    copy.set(src);
    return copy;
  }
  if ('file' in source) {
    const buf = await source.file.arrayBuffer();
    return new Uint8Array(buf); // arrayBuffer() already returns a fresh buffer
  }
  const url = resolvePdfUrlUnsafe(source);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`PDF fetch failed: ${res.status} ${url}`);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

/**
 * Resolve a PdfSource to a uniform LoadedPdf. Handles url / bytes / file /
 * docId. For docId, fetches `https://api.okrapdf.com/v1/documents/{docId}/original.pdf`
 * (publicly available for published docs).
 */
async function loadPdfPromise(source: PdfSource, opts: LoadOptions = {}): Promise<LoadedPdf> {
  const scale = opts.scale ?? DEFAULT_SCALE;
  const [pdfjs, bytes] = await Promise.all([loadPdfjs(), fetchSourceBytes(source)]);
  const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
  return {
    numPages: pdfDoc.numPages,
    async getPage(n: number): Promise<LoadedPage> {
      const page = await pdfDoc.getPage(n);
      const viewport = page.getViewport({ scale });
      return {
        pageNumber: n,
        viewport: { width: viewport.width, height: viewport.height },
        async render(ctx: CanvasRenderingContext2D, signal?: AbortSignal): Promise<void> {
          // Codex P2 (round 7): make renders cancellable. pdf.js's RenderTask
          // exposes .cancel(); we wire AbortSignal to it. If already aborted
          // when called, bail before kicking off the task.
          if (signal?.aborted) throw new DOMException('Render aborted', 'AbortError');
          const task = page.render({ canvasContext: ctx, viewport, canvas: ctx.canvas });
          const onAbort = () => task.cancel();
          signal?.addEventListener('abort', onAbort, { once: true });
          try {
            await task.promise;
          } finally {
            signal?.removeEventListener('abort', onAbort);
          }
        },
      };
    },
  };
}

export function loadPdfEffect(
  source: PdfSource,
  opts: LoadOptions = {},
): Effect.Effect<LoadedPdf, PdfViewerRuntimeError> {
  return Effect.tryPromise({
    try: () => loadPdfPromise(source, opts),
    catch: (cause) => pdfViewerFailure('load', cause),
  });
}

export function loadPdf(source: PdfSource, opts: LoadOptions = {}): Promise<LoadedPdf> {
  return Effect.runPromise(restorePdfViewerError(loadPdfEffect(source, opts)));
}

/**
 * Resolve a docId-style PdfSource to the public original.pdf URL. Exposed
 * separately so callers that already have a URL (e.g. pre-signed share
 * links) can build the fetch themselves.
 */
// Base used to resolve {docId} sources. Defaults to the okra cloud; OSS/self-host
// consumers can repoint it with setDocResolverBase().
let docResolverBase = 'https://api.okrapdf.com/v1';

/** Override the base URL used to resolve `{ docId }` PdfSource into a PDF URL. */
export function setDocResolverBase(base: string): void {
  Effect.runSync(setDocResolverBaseEffect(base));
}

export function setDocResolverBaseEffect(base: string): Effect.Effect<void> {
  return Effect.sync(() => {
    docResolverBase = base.replace(/\/+$/, '');
  });
}

function resolvePdfUrlUnsafe(source: PdfSource): string {
  if ('url' in source) return source.url;
  if ('docId' in source) return `${docResolverBase}/documents/${source.docId}/original.pdf`;
  throw new Error(
    'resolvePdfUrl: source has no URL (bytes/file sources must call loadPdf directly)',
  );
}

export function resolvePdfUrlEffect(
  source: PdfSource,
): Effect.Effect<string, PdfViewerRuntimeError> {
  return Effect.try({
    try: () => resolvePdfUrlUnsafe(source),
    catch: (cause) => pdfViewerFailure('resolve', cause),
  });
}

export function resolvePdfUrl(source: PdfSource): string {
  return Effect.runSync(restorePdfViewerError(resolvePdfUrlEffect(source)));
}
